// Reine Reducer + Optionslisten für semantische Dachobjekte (Stage 2).
// Operieren auf RoofSemanticData; KEIN React, KEINE Seiteneffekte → unit-testbar.
import type {
  RoofSemanticData,
  RoofEdge,
  RoofEdgeType,
  SemanticKeepoutZone,
  ExclusionKind,
  LatLngPoint,
} from "@energyos/shared-types";

/** Auswahl der zeichenbaren Dachkanten-Typen (Stage 2) + Label. */
export const EDGE_TYPE_OPTIONS: ReadonlyArray<{ value: RoofEdgeType; label: string }> = [
  { value: "first", label: "First" },
  { value: "traufe", label: "Traufe" },
  { value: "ortgang", label: "Ortgang" },
  { value: "grat", label: "Grat" },
  { value: "kehle", label: "Kehle" },
  { value: "dachbruch", label: "Dachbruch" },
];

/** Auswahl der Sperrflächen-Gründe (ExclusionKind) + Label. */
export const KEEPOUT_REASON_OPTIONS: ReadonlyArray<{ value: ExclusionKind; label: string }> = [
  { value: "edge_setback", label: "Randabstand" },
  { value: "maintenance_path", label: "Wartungsgang" },
  { value: "fire_setback", label: "Kaminabstand" },
  { value: "snow_guard_zone", label: "Schneefang" },
  { value: "safety_line", label: "Blitzschutz" },
  { value: "keepout", label: "No-Go" },
];

export function edgeTypeLabel(t: RoofEdgeType): string {
  return EDGE_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t;
}
export function keepoutReasonLabel(k: ExclusionKind): string {
  return KEEPOUT_REASON_OPTIONS.find((o) => o.value === k)?.label ?? k;
}

/** Setzt edges/keepouts immutabel zurück (für Reducer-Komposition). */
function withEdges(d: RoofSemanticData, edges: RoofEdge[]): RoofSemanticData {
  return { ...d, edges };
}
function withKeepouts(d: RoofSemanticData, keepouts: SemanticKeepoutZone[]): RoofSemanticData {
  return { ...d, keepouts };
}

export function addEdge(
  d: RoofSemanticData,
  id: string,
  type: RoofEdgeType,
  points: LatLngPoint[],
): RoofSemanticData {
  return withEdges(d, [...d.edges, { id, type, points }]);
}

export function updateEdge(
  d: RoofSemanticData,
  id: string,
  patch: Partial<Omit<RoofEdge, "id">>,
): RoofSemanticData {
  return withEdges(
    d,
    d.edges.map((e) => (e.id === id ? { ...e, ...patch } : e)),
  );
}

export function removeEdge(d: RoofSemanticData, id: string): RoofSemanticData {
  return withEdges(d, d.edges.filter((e) => e.id !== id));
}

export function addKeepout(
  d: RoofSemanticData,
  id: string,
  kind: ExclusionKind,
  points: LatLngPoint[],
): RoofSemanticData {
  return withKeepouts(d, [...d.keepouts, { id, kind, points }]);
}

export function updateKeepout(
  d: RoofSemanticData,
  id: string,
  patch: Partial<Omit<SemanticKeepoutZone, "id">>,
): RoofSemanticData {
  return withKeepouts(
    d,
    d.keepouts.map((k) => (k.id === id ? { ...k, ...patch } : k)),
  );
}

export function removeKeepout(d: RoofSemanticData, id: string): RoofSemanticData {
  return withKeepouts(d, d.keepouts.filter((k) => k.id !== id));
}

/** Schaltet ein Modul (Zell-ID „row-col") der Auto-Belegung an/aus (immutabel). */
export function toggleModuleDisabled(d: RoofSemanticData, cellId: string): RoofSemanticData {
  const disabled = d.disabledModules.includes(cellId)
    ? d.disabledModules.filter((x) => x !== cellId)
    : [...d.disabledModules, cellId];
  return { ...d, disabledModules: disabled };
}
